/**
 * Platform granular permission hooks — GET /api/v1/platform/permissions/me.
 *
 * The endpoint returns the granular roles (e.g., `cac_approver`) plus the
 * computed monitoring access (`MonitoringAccess:platform#{view,admin}`) used
 * to show the Flower ("Workers") entry point (Sprint 209 / ISS-358).
 *
 * Fail-closed (SEC-003): while loading or on error, roles resolve to [] and
 * monitoring is undefined — callers must treat undefined as DENIED.
 */

import { useQuery } from '@tanstack/react-query';
import { rawApi } from '@/lib/api-client';

export interface MonitoringPermissions {
  view: boolean;
  admin: boolean;
}

export interface MePermissionsResponse {
  roles: string[];
  monitoring: MonitoringPermissions;
}

export interface PlatformPermissionsState {
  roles: string[];
  monitoring: MonitoringPermissions | undefined;
  loading: boolean;
}

export function usePlatformPermissions(): PlatformPermissionsState {
  const { data, isLoading } = useQuery({
    queryKey: ['platform-permissions', 'me'],
    queryFn: async () => {
      const response = await rawApi.get<MePermissionsResponse>(
        'platform/permissions/me'
      );
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  return {
    roles: data?.roles ?? [],
    monitoring: data?.monitoring,
    loading: isLoading,
  };
}

export function useHasPlatformRole(role: string): boolean {
  const { roles } = usePlatformPermissions();
  return roles.includes(role);
}

/**
 * Resolve the monitoring visibility flag from the hook payload. Undefined
 * (loading or error) resolves to false — fail-closed.
 */
export function hasMonitoringAccess(
  monitoring: MonitoringPermissions | undefined
): boolean {
  return monitoring?.view === true;
}
